import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiEndpoints } from '../services/api';
import DocumentForm from '../components/DocumentForm';
import DocViewer from '../components/DocViewer';
import { useAppContext } from '../contexts/AppContext';
import CalendarPicker from '../components/CalendarPicker';

const DOC_LABEL_MAP = {
  rca:            'RCA',
  itp:            'ITP',
  casco:          'CASCO',
  rovinieta:      'ROVINIETA',
  roadAssistance: 'ROAD ASSISTANCE',
};

const computeDaysRemaining = (expiryDate) => {
  const s = String(expiryDate || '');
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  const exp = m ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`) : new Date(s);
  if (Number.isNaN(exp.getTime())) return 9999;
  return Math.ceil((exp.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
};

const parseFlexDate = (val) => {
  const s = String(val || '');
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`).getTime();
  const t = new Date(s).getTime();
  return isNaN(t) ? Infinity : t;
};

const parseFlexibleDate = (value) => {
  if (!value) return null;
  const s = String(value);
  const m1 = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m1) return new Date(`${m1[3]}-${m1[2]}-${m1[1]}T00:00:00`);
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) return new Date(`${m2[1]}-${m2[2]}-${m2[3]}T00:00:00`);
  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatExpiryDate = (expiryDate) => {
  const s = String(expiryDate || '');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(s)) return s;
  return new Date(s).toLocaleDateString('en-GB');
};

const getDocumentState = (doc) => {
  const days = typeof doc.daysRemaining === 'number' ? doc.daysRemaining : computeDaysRemaining(doc.expiryDate);
  if (days < 7) return 'critical';
  if (days < 30) return 'attention';
  return 'ok';
};

const getStatusColor = (state) => {
  if (state === 'critical') return '#dc2626';
  if (state === 'attention') return '#f59e0b';
  return '#10b981';
};

const getStatusLabel = (state) => {
  if (state === 'critical') return 'Critical';
  if (state === 'attention') return 'Expiring soon';
  return 'Activ';
};

const getFilePreviewUrl = (doc) => {
  if (!doc.fileUrl) return null;
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
  return `${apiUrl}/static/${doc.fileUrl}`;
};

const Alerts = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAppContext();
  const [vehicles, setVehicles] = useState([]);
  const [collectionDocs, setCollectionDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [reminderDate, setReminderDate] = useState('');
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderSent, setReminderSent] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [pushStatus, setPushStatus] = useState(null);   // GET /notifications/status (admin)
  const [pushSending, setPushSending] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const [vehicleRes, docsRes] = await Promise.all([
        apiEndpoints.vehicles.list(),
        apiEndpoints.documents.list(),
      ]);
      setVehicles(vehicleRes.data || []);
      setCollectionDocs(docsRes.data || []);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) apiEndpoints.notifications.status().then((res) => setPushStatus(res.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    fetchVehicles();
    const handler = () => fetchVehicles();
    window.addEventListener('fleet:docs-changed', handler);
    return () => window.removeEventListener('fleet:docs-changed', handler);
  }, []);

  // Merge the Documents collection (canonical store — the same source the
  // Dashboard uses) with the legacy vehicle.docs snapshots. The backend keeps
  // both in sync, so they describe the same documents and must be de-duplicated
  // on vehicle + document type. (Previously the embedded entries were keyed
  // "<vehicleId>_<type>" while collection records were keyed by their Mongo
  // _id, so nothing ever matched and every alert was listed twice.)
  const documents = useMemo(() => {
    const vehicleById    = new Map(vehicles.map((v) => [v.id, v]));
    const vehicleByPlate = new Map(vehicles.filter((v) => v.plate).map((v) => [v.plate, v]));
    const byKey = new Map();
    const classify = (days) => (days < 7 ? 'critical' : days < 30 ? 'expiring' : 'ok');

    // 1. Documents collection first — these carry the real _id (needed for
    //    reminders) and the issue date.
    for (const doc of collectionDocs) {
      // Skip supplier-linked documents (not vehicle alerts)
      if (doc.vehicleId && String(doc.vehicleId).startsWith('supplier-')) continue;
      const vehicle = vehicleById.get(doc.vehicleId) || vehicleByPlate.get(doc.plate) || null;
      const docType = String(doc.documentType || '').trim().toUpperCase();
      const owner   = vehicle ? vehicle.id : (String(doc.plate || '').toUpperCase() || String(doc._id));
      const key     = `${owner}|${docType}`;
      const days    = computeDaysRemaining(doc.expiryDate);
      const alert = {
        _id: String(doc._id),
        vehicleId: vehicle ? vehicle.id : (doc.vehicleId || ''),
        plate: doc.plate || vehicle?.plate || '',
        company: vehicle?.company || doc.company || '',
        driver: vehicle?.driverName || doc.driver || '',
        documentType: docType,
        issueDate: doc.issueDate || '',
        expiryDate: doc.expiryDate,
        issuer: doc.issuer && doc.issuer !== '—' ? doc.issuer : '',
        status: classify(days),
        daysRemaining: days,
        fileUrl: doc.fileUrl || '',
      };
      const existing = byKey.get(key);
      if (!existing) { byKey.set(key, alert); continue; }
      // Two collection records for the same vehicle + type: keep the newest expiry.
      const next = parseFlexDate(alert.expiryDate);
      const prev = parseFlexDate(existing.expiryDate);
      if (Number.isFinite(next) && (!Number.isFinite(prev) || next > prev)) byKey.set(key, alert);
    }

    // 2. Embedded vehicle.docs — only for vehicle + type pairs that have no
    //    collection record (vehicles saved before the sync existed).
    for (const vehicle of vehicles) {
      const vDocs = vehicle.docs || {};
      for (const [docKey, label] of Object.entries(DOC_LABEL_MAP)) {
        const doc = vDocs[docKey];
        if (!doc || !doc.exp) continue;
        const key = `${vehicle.id}|${label}`;
        if (byKey.has(key)) continue;
        const days = computeDaysRemaining(doc.exp);
        byKey.set(key, {
          _id: `${vehicle.id}_${docKey}`,
          vehicleId: vehicle.id,
          plate: vehicle.plate || '',
          company: vehicle.company || '',
          driver: vehicle.driverName || '',
          documentType: label,
          issueDate: doc.issued || '',
          expiryDate: doc.exp,
          issuer: doc.issuer && doc.issuer !== '—' ? doc.issuer : '',
          status: classify(days),
          daysRemaining: days,
          fileUrl: doc.fileUrl || '',
        });
      }
    }

    return Array.from(byKey.values());
  }, [vehicles, collectionDocs]);

  const alertDocs    = useMemo(() => [...documents].sort((a, b) => parseFlexDate(a.expiryDate) - parseFlexDate(b.expiryDate)).filter((d) => getDocumentState(d) !== 'ok'), [documents]);
  const criticalDocs = useMemo(() => documents.filter((d) => getDocumentState(d) === 'critical'), [documents]);
  const attentionDocs = useMemo(() => documents.filter((d) => getDocumentState(d) === 'attention'), [documents]);
  const okDocs       = useMemo(() => documents.filter((d) => getDocumentState(d) === 'ok'), [documents]);

  const handleRenewDocument = (document) => {
    setEditingDocument(document);
    setShowDocumentForm(true);
  };

  const handleSaveDocument = async (documentData, file) => {
    try {
      await apiEndpoints.documents.create(documentData, file);
      await fetchVehicles();
      window.dispatchEvent(new CustomEvent('fleet:docs-changed'));
      setShowDocumentForm(false);
    } catch (err) {
      console.error('Failed to save document:', err);
      alert('Error saving document. Please try again.');
    }
  };

  const handleSendReminder = async () => {
    if (!reminderDate) return;
    setReminderSending(true);
    try {
      await apiEndpoints.reminders.create({
        vehicleId:    selectedAlert.vehicleId,
        documentId:   selectedAlert._id,
        plate:        selectedAlert.plate,
        documentType: selectedAlert.documentType,
        scheduledDate: reminderDate,
        message: `Reminder: ${selectedAlert.documentType} for vehicle ${selectedAlert.plate} expires on ${formatExpiryDate(selectedAlert.expiryDate)}.`,
      });
      setReminderSent(true);
      setTimeout(() => setReminderSent(false), 3000);
      setReminderDate('');
    } catch (err) {
      console.error('Failed to send reminder:', err);
      alert('Error sending reminder.');
    } finally {
      setReminderSending(false);
    }
  };

  // Runs the daily alert job now: one push per driver about their vehicles'
  // expiring documents, plus the critical summary to admins.
  const handleSendNotifications = async () => {
    setPushSending(true);
    setPushResult(null);
    try {
      const res = await apiEndpoints.notifications.sendAlerts();
      const r = res.data;
      if (!r.firebase?.ready) {
        setPushResult({ ok: false, text: `Nothing sent — push is not configured on the server (${r.firebase?.error || 'Firebase key missing'}).` });
      } else {
        const parts = [`Drivers: ${r.drivers.notified}/${r.drivers.withAlerts} notified`];
        if (r.drivers.withoutDevice) parts.push(`${r.drivers.withoutDevice} without the app`);
        parts.push(`Admins: ${r.admins.sent}/${r.admins.devices} device(s)`);
        if (r.reminders.due) parts.push(`Reminders: ${r.reminders.sent}/${r.reminders.due}`);
        // Surface FCM errors (e.g. app/invalid-credential) so a failed setup is obvious.
        const errors = [...new Set([...(r.drivers.details || []).flatMap((d) => d.errors || []), ...(r.admins.errors || [])])];
        if (errors.length) parts.push(`errors: ${errors.join(', ')}`);
        const attempted = r.drivers.details.some((d) => d.devices > 0) || r.admins.devices > 0;
        setPushResult({ ok: !errors.length && (!attempted || r.drivers.notified > 0 || r.admins.sent > 0), text: parts.join(' · ') });
      }
      apiEndpoints.notifications.status().then((s) => setPushStatus(s.data)).catch(() => {});
    } catch (err) {
      setPushResult({ ok: false, text: err?.response?.data?.message || err.message || 'Failed to send notifications.' });
    } finally {
      setPushSending(false);
    }
  };

  const handleViewDocument = (doc) => {
    const url = getFilePreviewUrl(doc);
    if (!url) { alert('No file attached to this document.'); return; }
    setPreviewDoc({ url, filename: doc.fileUrl?.split('/').pop() || 'document' });
  };

  if (loading) return <div className="page-loading">Loading alerts...</div>;

  if (selectedAlert) {
    const state = getDocumentState(selectedAlert);
    const statusColor = getStatusColor(state);
    const issueDate  = parseFlexibleDate(selectedAlert.issueDate)?.toLocaleDateString() || '—';
    const expiryDate = parseFlexibleDate(selectedAlert.expiryDate)?.toLocaleDateString() || '—';
    const days = typeof selectedAlert.daysRemaining === 'number'
      ? selectedAlert.daysRemaining
      : computeDaysRemaining(selectedAlert.expiryDate);
    const daysText = days < 0 ? `${Math.abs(days)} days ago` : `${days} days left`;

    return (
      <div>
        <button className="back-btn" onClick={() => { setSelectedAlert(null); setReminderDate(''); setReminderSent(false); }}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to alerts
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate">
            <span className="plate-pill">{selectedAlert.plate || 'UNKNOWN'}</span>
          </div>
          <div className="vprofile-model">{selectedAlert.documentType || 'Alert'}</div>
          <div className="vprofile-sub">{selectedAlert.issuer || 'Unknown issuer'} • expires {expiryDate}</div>
        </div>

        <div className="detail-grid">
          <div className="detail-left">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Alert Details</div>
                <span style={{ padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, color: statusColor, background: `${statusColor}18` }}>{getStatusLabel(state)}</span>
              </div>
              <div className="card-body">
                <div className="info-row"><div className="info-lbl">Vehicle</div><div className="info-val">{selectedAlert.plate || '—'}</div></div>
                <div className="info-row"><div className="info-lbl">Document type</div><div className="info-val">{selectedAlert.documentType || '—'}</div></div>
                <div className="info-row"><div className="info-lbl">Issuer</div><div className="info-val">{selectedAlert.issuer || '—'}</div></div>
                <div className="info-row"><div className="info-lbl">Issued on</div><div className="info-val">{issueDate}</div></div>
                <div className="info-row"><div className="info-lbl">Expires on</div><div className="info-val">{expiryDate}</div></div>
                <div className="info-row"><div className="info-lbl">Driver</div><div className="info-val">{selectedAlert.driver || '—'}</div></div>
                <div className="info-row" style={{ borderBottom: 'none' }}><div className="info-lbl">Time remaining</div><div className="info-val" style={{ color: statusColor, fontWeight: 700 }}>{daysText}</div></div>
              </div>
            </div>
          </div>

          <div className="detail-right">
            <div className="card quick-actions-card">
              <div className="card-header"><div className="card-title">Quick Actions</div></div>
              <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
                {isAdmin && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Send Reminder to Driver</div>
                    <div style={{ marginBottom: 6 }}>
                      <CalendarPicker
                        name="reminderDate"
                        value={reminderDate}
                        onChange={(e) => setReminderDate(e.target.value)}
                        placeholder="Pick reminder date…"
                      />
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      disabled={!reminderDate || reminderSending}
                      onClick={handleSendReminder}
                    >
                      {reminderSending ? 'Sending…' : reminderSent ? '✓ Sent!' : 'Send Reminder'}
                    </button>
                    {reminderSent && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#10b981', textAlign: 'center' }}>
                        Reminder scheduled for {reminderDate}
                      </div>
                    )}
                  </div>
                )}
                {getFilePreviewUrl(selectedAlert) && (
                  <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => handleViewDocument(selectedAlert)}>View Document</button>
                )}
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => navigate('/events')}>Open Activity</button>
                {isAdmin && (
                  <button className="btn btn-danger" style={{ width: '100%' }} onClick={() => handleRenewDocument(selectedAlert)}>Renew Document</button>
                )}
              </div>
            </div>
          </div>
        </div>

        <DocumentForm
          isOpen={showDocumentForm}
          onClose={() => setShowDocumentForm(false)}
          document={editingDocument}
          onSave={handleSaveDocument}
        />
        <DocViewer url={previewDoc?.url} filename={previewDoc?.filename} onClose={() => setPreviewDoc(null)} />
      </div>
    );
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Fleet Alerts</div>
          <div className="ph-sub">Documents requiring attention</div>
        </div>
        {isAdmin && alertDocs.length > 0 && (
          <button className="btn btn-primary btn-sm" onClick={handleSendNotifications} disabled={pushSending}>
            {pushSending ? 'Sending…' : 'Send Notifications'}
          </button>
        )}
      </div>

      {isAdmin && pushStatus && (
        <div style={{ margin: '-6px 0 14px', fontSize: 12, color: pushStatus.firebase?.ready ? 'var(--text-3)' : '#b45309' }}>
          {pushStatus.firebase?.ready
            ? `Push notifications: ready · ${pushStatus.devices.driver} driver device(s), ${pushStatus.devices.admin} admin device(s) · ${pushStatus.alerts.driversWithAlerts} driver(s) with alerts, ${pushStatus.alerts.driversWithAlertsAndDevice} of them with the app · sent daily at 08:00`
            : `Push notifications are not configured on the server — ${pushStatus.firebase?.error || 'Firebase key missing'}`}
          {pushResult && (
            <div style={{ marginTop: 4, fontWeight: 600, color: pushResult.ok ? '#059669' : '#dc2626' }}>{pushResult.text}</div>
          )}
        </div>
      )}

      <div className="alert-status-grid">
        <div className="alert-status-card alert-status-critical">
          <div className="alert-status-label">CRITICAL (&lt;7 DAYS)</div>
          <div className="alert-status-count">{criticalDocs.length}</div>
          <div className="alert-status-text">Immediate action</div>
        </div>
        <div className="alert-status-card alert-status-attention">
          <div className="alert-status-label">EXPIRING SOON (7-30 DAYS)</div>
          <div className="alert-status-count">{attentionDocs.length}</div>
          <div className="alert-status-text">Plan renewal</div>
        </div>
        <div className="alert-status-card alert-status-ok">
          <div className="alert-status-label">ACTIV (&gt;30 DAYS)</div>
          <div className="alert-status-count">{okDocs.length}</div>
          <div className="alert-status-text">All other documents</div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="alerts-list">
            {alertDocs.length === 0 ? (
              <div className="empty-row">No alerts at this time.</div>
            ) : (
              alertDocs.map((doc, index) => {
                const state = getDocumentState(doc);
                const days = typeof doc.daysRemaining === 'number' ? doc.daysRemaining : computeDaysRemaining(doc.expiryDate);
                const daysText = days < 0 ? `${Math.abs(days)}d ago` : `${days}d left`;
                return (
                  <div key={doc._id || index} className={`alert-item alert-item-${state}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedAlert(doc)}>
                    <div className="alert-item-left">
                      <div className={`alert-item-indicator alert-indicator-${state}`} />
                      <div className="alert-item-plate">{doc.plate || 'N/A'}</div>
                    </div>
                    <div className="alert-item-content">
                      <div className="alert-item-title">
                        <span className="alert-item-driver">{doc.driver || '—'}</span>
                        <span className="alert-item-company">—</span>
                        <span className="alert-item-company">{doc.company || '—'}</span>
                      </div>
                      <div className="alert-item-meta">
                        <span className="alert-doc-type">{doc.documentType || 'DOC'}</span>
                        <span>— issued by {doc.issuer || '—'}</span>
                        <span>— expires {formatExpiryDate(doc.expiryDate)}</span>
                      </div>
                    </div>
                    <div className="alert-item-days">
                      <div className={`alert-item-days-value alert-days-${state}`}>{daysText}</div>
                    </div>
                    <div className={`alert-item-badge alert-badge-${state}`}>
                      {state === 'critical' ? 'Critical' : 'Expiring soon'}
                    </div>
                    <div className="alert-item-actions">
                      <button className="btn btn-ghost btn-xs" style={{ color: '#6b7280' }} onClick={(e) => { e.stopPropagation(); setSelectedAlert(doc); }}>View</button>
                      {isAdmin && (
                        <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); handleRenewDocument(doc); }}>Renewal</button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <DocumentForm
        isOpen={showDocumentForm}
        onClose={() => setShowDocumentForm(false)}
        document={editingDocument}
        onSave={handleSaveDocument}
      />
      <DocViewer url={previewDoc?.url} filename={previewDoc?.filename} onClose={() => setPreviewDoc(null)} />
    </div>
  );
};

export default Alerts;
